import React, { Fragment, useState, useEffect } from 'react';
import { Row, Col } from 'reactstrap';
import { _getSubways, _getZonedSchools } from '../../../../../shared/helpers/apicalls';
import { _capitalizeText } from '../../../../../shared/helpers/utils';

const SubwayLines = ({ lines }) => {
	if (!lines || !Array.isArray(lines)) return null;
	return (
		<span>
			{lines.map(line => {
				if (line.toLowerCase().includes('express')) {
					return (
						<span className="subway-line-express" key={line}>
							<span className={`subway-line line-${line.toLowerCase()}`}></span>
							<span className="text">{line.replace(' Express', '')}</span>
						</span>
					);
				}
				return <span className={`subway-line line-${line.toLowerCase()}`} key={line}>{line}</span>;
			})}
		</span>
	);
};

const NearbySubwaysWidget = ({ subways }) => {
	if (!subways || Object.keys(subways).length === 0) return null;
	return (
		<div>
			<div className="font-weight-bold" style={{ fontSize: 14 }}>Nearby Subways</div>
			<p style={{ fontSize: 12, color: '#999', fontStyle: 'italic' }}>Nearby subway entrances around this building.</p>
			{Object.keys(subways).slice(0, 5).map((key, i) => {
				const station = subways[key];
				const geoStr = station.geocode ? station.geocode.trim().replace(',', '') : '';
				const formattedGeo = geoStr.slice(geoStr.trim().slice(0, geoStr.indexOf(' ') + ', ' + geoStr.indexOf(' ')));
				const walkTime = Math.round(station.distance * 20);
				return (
					<div className="subway-station" key={key + '-' + i} style={{ display: 'flex', alignItems: 'center', padding: '6px 0', fontSize: 12 }}>
						<div style={{ minWidth: '50%' }}>
							<SubwayLines lines={station.subway_lines} />
						</div>
						<div style={{ textAlign: 'left' }}>
							<a href={`https://www.google.com/maps/place/${formattedGeo}`} target="_blank" rel="noopener noreferrer">
								{_capitalizeText(station.station_name)}
							</a>
							<br />
							<span style={{ display: 'inline-block', width: 71 }}>{walkTime} min walk</span>
							<span style={{ color: '#ccc' }}> | </span>
							{station.distance} mi
						</div>
					</div>
				);
			})}
		</div>
	);
};

const formatSchools = (schoolZone) => {
	if (schoolZone && schoolZone.schools && Array.isArray(schoolZone.schools)) {
		return schoolZone.schools.map((school, i) => {
			const code = schoolZone.dbn && schoolZone.dbn[i] ? schoolZone.dbn[i].slice(2) : false;
			if (code) {
				return (
					<div key={school + '-' + i}>
						<a href={`https://www.schools.nyc.gov/schools/${code}`} target="_blank" rel="noopener noreferrer">
							{_capitalizeText(school)} <i className="fa fa-external-link" style={{ fontSize: 10 }}></i>
						</a>
					</div>
				);
			}
			return <div key={school + '-' + i}>{_capitalizeText(school)}</div>;
		});
	}
	return '--';
};

const SchoolSection = ({ label, zone }) => (
	<Fragment>
		<div className="font-weight-bold" style={{ fontSize: 12, paddingTop: 10 }}>{label}</div>
		<div style={{ fontSize: 12 }}>
			<Row>
				<Col xs={3}>District</Col>
				<Col xs={9} className="font-weight-bold">
					{zone && zone.school_district ? zone.school_district : '--'}
				</Col>
				<Col xs={3}>School</Col>
				<Col xs={9} className="font-weight-bold">
					{zone && formatSchools(zone) !== '--'
						? formatSchools(zone)
						: zone && zone.remarks ? zone.remarks : '--'}
				</Col>
			</Row>
		</div>
	</Fragment>
);

const ZonedSchoolsWidget = ({ schoolZones }) => {
	if (!schoolZones || Object.keys(schoolZones).length === 0) return null;
	return (
		<div>
			<div className="font-weight-bold" style={{ fontSize: 14 }}>Zoned Schools</div>
			<p style={{ fontSize: 12, color: '#999', fontStyle: 'italic' }}>Zoned schools for residents of this building.</p>
			<SchoolSection label="Elementary" zone={schoolZones['elementary']} />
			<SchoolSection label="Middle School" zone={schoolZones['middle']} />
			<SchoolSection label="High School" zone={schoolZones['high']} />
		</div>
	);
};

const InTheArea = ({ buildingRes }) => {
	const [subways, setSubways] = useState(null);
	const [schoolZones, setSchoolZones] = useState(null);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		if (!buildingRes || !buildingRes.geocode) {
			setLoading(false);
			return;
		}
		let mounted = true;
		const fetchData = async () => {
			const [subwayData, zoneData] = await Promise.all([
				_getSubways(buildingRes),
				_getZonedSchools(buildingRes),
			]);
			if (mounted) {
				setSubways(subwayData);
				setSchoolZones(zoneData);
				setLoading(false);
			}
		};
		fetchData();
		return () => { mounted = false; };
	}, [buildingRes && buildingRes.geocode]);

	if (loading) return null;
	const hasSubways = subways && Object.keys(subways).length > 0;
	const hasSchools = schoolZones && Object.keys(schoolZones).length > 0;
	if (!hasSubways && !hasSchools) return null;

	return (
		<Fragment>
			<Row>
				<Col md={12}>
					<h3 className="section-title section-title--not-last">In the Area</h3>
					<h3 className="section-title section-title--not-last subhead"> </h3>
					<hr/>
				</Col>
			</Row>
			<Row>
				{hasSubways &&
					<Col md={6} xs={12} className="mb-3">
						<NearbySubwaysWidget subways={subways} />
					</Col>
				}
				{hasSchools &&
					<Col md={6} xs={12} className="mb-3">
						<ZonedSchoolsWidget schoolZones={schoolZones} />
					</Col>
				}
			</Row>
		</Fragment>
	);
};

export default InTheArea;
